/** Formatacao e mascaras de entrada no padrao brasileiro. */

export const soDigitos = (v: string) => v.replace(/\D/g, '');

export function formatarCpf(v: string | null): string {
  if (!v) return '';
  const d = soDigitos(v).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

export function formatarTelefone(v: string | null): string {
  if (!v) return '';
  const d = soDigitos(v).slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

export function formatarCep(v: string | null): string {
  if (!v) return '';
  return soDigitos(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');
}

/** '1972-10-14' -> '14/10/1972'. Sem Date: evita o pulo de um dia por fuso. */
export function dataBr(iso: string | null): string {
  if (!iso) return '';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : '';
}

/** Vai pondo as barras enquanto a pessoa digita: '14101972' -> '14/10/1972'.
 *
 *  Serve ao campo de data que se digita em vez de escolher no calendario. No
 *  celular, `type="date"` abre o seletor de rolagem, e chegar a 1972 rolando
 *  ano por ano e o tipo de coisa que faz alguem desistir da consulta. */
export function mascaraData(bruto: string): string {
  const d = soDigitos(bruto).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** '14/10/1972' -> '1972-10-14'. Vazio quando ainda nao da uma data.
 *
 *  Confere o dia depois de montar: '31/02/1990' passa em qualquer teste de
 *  faixa -- 31 e dia, 02 e mes -- e mesmo assim nao existe. Sem esta volta, o
 *  paciente receberia "nao encontramos" e culparia o numero do exame. */
export function dataBrParaIso(texto: string): string {
  const d = soDigitos(texto);
  if (d.length !== 8) return '';
  const dia = d.slice(0, 2);
  const mes = d.slice(2, 4);
  const ano = d.slice(4);
  const iso = `${ano}-${mes}-${dia}`;
  const data = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(data.getTime())) return '';
  return data.getUTCDate() === Number(dia) && data.getUTCMonth() + 1 === Number(mes)
    ? iso
    : '';
}

/** 2026-08-04 -> 04/08/26. Usado nas listas, onde o seculo nao acrescenta
 *  nada e cada caractere disputa espaco com o nome do paciente. */
export function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a.slice(2)}` : '';
}

export function idade(nascimento: string | null): number | null {
  if (!nascimento) return null;
  const [a, m, d] = nascimento.slice(0, 10).split('-').map(Number);
  if (!a || !m || !d) return null;
  const hoje = new Date();
  let anos = hoje.getFullYear() - a;
  const mesHoje = hoje.getMonth() + 1;
  if (mesHoje < m || (mesHoje === m && hoje.getDate() < d)) anos--;
  return anos;
}

export function formatarCnpj(v: string | null): string {
  if (!v) return '';
  return soDigitos(v)
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/** Valor em reais. Devolve travessão quando não há valor, para a coluna da
 *  tabela não ficar com célula vazia sem explicação. */
export function dinheiro(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Com o R$ na frente. Para os cartoes de total dos relatorios, onde o numero
 *  aparece sozinho e grande -- ali a moeda precisa estar dita.
 *
 *  Nas celulas da tabela continua-se usando `dinheiro`: repetir "R$" em cada
 *  linha de uma coluna de valores so enche a coluna e atrapalha a leitura de
 *  cima para baixo. */
export function dinheiroBr(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `R$ ${dinheiro(v)}`;
}

/** Número para campo de dinheiro editável: sempre com dois decimais e vírgula.
 *  Sem isso, um valor de 150 volta da tabela como "150" num campo que espera
 *  "150,00" e parece que os centavos se perderam. */
export function paraCampoDinheiro(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return v.toFixed(2).replace('.', ',');
}

/** Data de hoje em ISO, no fuso local — `toISOString()` volta em UTC e, à
 *  noite, devolve o dia seguinte. */
export function hojeIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Primeiro dia do mês corrente. É onde a fila abre, por escolha da clínica.
 *
 *  Tem um preço, e ele fica escrito aqui porque não aparece na tela: um exame
 *  do dia 31 que ainda espera laudo sai do recorte no dia 1º, e some junto das
 *  contagens das abas -- inclusive da "Aguardando laudo", que é onde alguém
 *  perceberia. Na virada do mês vale puxar a data para trás e conferir se
 *  ninguém ficou. */
export function primeiroDiaDoMes(): string {
  return `${hojeIso().slice(0, 7)}-01`;
}

/** Data de N dias atrás, em ISO local. */
export function diasAtras(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ultimoDiaDoMes(): string {
  const d = new Date();
  const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${ultimo.getFullYear()}-${String(ultimo.getMonth() + 1).padStart(2, '0')}-${String(ultimo.getDate()).padStart(2, '0')}`;
}

export const TIPOS_EXAME: Record<string, string> = {
  ADMISSIONAL: 'Admissional',
  PERIODICO: 'Periódico',
  DEMISSIONAL: 'Demissional',
  MUDANCA_FUNCAO: 'Mudança de função',
  RETORNO_TRABALHO: 'Retorno ao trabalho',
  SOLICITACAO_MEDICA: 'Solicitação médica',
};

export const MODOS_ENVIO: Record<string, string> = {
  LAUDO_NO_SISTEMA: 'Laudar no sistema',
  JA_LAUDADO: 'Enviar já laudado',
  DEPOIS_DE_LAUDADO: 'Enviar depois de laudado',
  // O paciente leva só a imagem e o laudo é feito pelo médico dele.
  SEM_LAUDO: 'Sem laudo — só a imagem',
  // O laudo sai por um portal de fora. Para esta clínica o exame nasce pronto.
  PORTAL: 'Portal',
};

/** Modos em que a clínica entrega a folha com as imagens, e não um laudo. */
export const temDocumentoSemLaudo = (modo: string | null | undefined): boolean =>
  modo === 'SEM_LAUDO' || modo === 'PORTAL';

export const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const;

export const LETRAS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
